<script setup lang="ts">
import { ref } from 'vue'
import { storeToRefs } from 'pinia'

import { CategoryFull, FilterItem } from '~/js/interfaces/facets'
import { getClasses } from '~/js/composables/classes'
import { getGlobalTranslation } from '~/js/composables/translations'
import { useTeambuildingState } from '~/js/stores/teambuildings-tm'

import FieldsetTeambuildingFilter from '~/vue/_teammasters/molecules/fieldsets/FieldsetTeambuildingFilter.vue'
import FieldsetTeambuildingSearch from '~/vue/_teammasters/molecules/fieldsets/FieldsetTeambuildingSearch.vue'
import ButtonTertiary from '~/vue/_teammasters/atoms/buttons/ButtonTertiary.vue'


interface Filter {
    type: string;
    values: Array<number | string>;
}

interface Props {
    activeFilter: string|null
    categories: Array<CategoryFull|null> | null
    defaultCategories: Array<number|null>
    defaultPurposes: Array<number|null>
    purposes: Array<CategoryFull|null> | null
    utilities?: string|null
}

const props = withDefaults(defineProps<Props>(), {utilities: '', activeFilter: 'categories'})
const emit = defineEmits(['search'])

const teambuildingStore = useTeambuildingState()
const { filters } = storeToRefs(teambuildingStore)
const openFilter = ref<string|null>(null)

const classes = {
    color: 'bg-black',
    display: 'grid grid-cols-1 md:grid-cols-5',
    rounded: 'rounded-lg md:rounded-l-lg md:rounded-r-full',
    spacing: 'p-2 gap-2',
}

const data = ref<Filter[]>([
    {
        type: 'categories',
        values: props.defaultCategories as Array<number | string>
    },
    {
        type: 'purposes',
        values: props.defaultPurposes as Array<number | string>
    },
])

const onToggleDropdown = (value: string|null) => {
    openFilter.value = value ?? null
}

const onHandleInput = (input: string) => {
    filters.value.searchQuery = input
    
    onSearch(false)
}

const onHandleFilter = (filterItem: FilterItem) => {
    const filter: Filter | undefined = data.value.find(val => val.type == filterItem.type)

    if (filter) {
        filter.values = filterItem.values

        onSearch(false)
    }
}

const onSearch = scrollTo => {
    emit('search', scrollTo)
}

</script>

<template>
    <form 
        :class="[
            getClasses(classes),
            utilities
        ]"
        @submit.prevent="() => onSearch(true)"
    >
        <template v-if="activeFilter == 'categories'">
            <FieldsetTeambuildingFilter 
                v-if="categories"
                :disabled="openFilter == 'purposes'"
                :label="getGlobalTranslation('Categories')"
                :options="categories" 
                @active-dropdown="onToggleDropdown($event)"
                @input="onHandleFilter($event)"
                dropdown-position="left"
                type="categories"
                utilities="md:col-span-2"
            />
            <FieldsetTeambuildingFilter 
                v-if="purposes"
                :disabled="openFilter == 'categories'"
                :label="getGlobalTranslation('Purposes')"
                :options="purposes"
                @active-dropdown="onToggleDropdown($event)"
                @input="onHandleFilter($event)"
                dropdown-position="right"
                type="purposes" 
                utilities="md:col-span-2"
            />
        </template>

        <template 
            v-if="activeFilter == 'search'"
        >
            <FieldsetTeambuildingSearch 
                :label="getGlobalTranslation('Name')"
                @input="onHandleInput($event)"
                type="categories"
                utilities="md:col-span-4"
            />
        </template>

        <ButtonTertiary 
            :content="getGlobalTranslation('Search')"
            :ga="{
                category: 'teambuilding-search',
                action: 'click',
                label: getGlobalTranslation('Search')
            }"
            type="submit"
            theme="dark"
            icon="fas fa-search"
            utilities="mx-auto"
        />
    </form>
</template>